import React from 'react';
import { Text, Tooltip, Icon } from '@perch-labs/shrike';
import { formatWithCommas, formatAsMoney } from '@perch-labs/warbler';
import Bolt from '/images/solar/bolt.svg';
import DollarSign from '/images/solar/dollar-sign.svg';
import DollarSignEmpty from '/images/solar/dollar-sign-empty.svg';
import Blocks from '/images/solar/blocks.svg';
import BlocksEmpty from '/images/solar/blocks-empty.svg';
import Home from '/images/solar/home.svg';
import { Card, Container, Hr, LabelAndTooltip } from 'styles';
import { copyFor } from 'config';
import { SPONSORED_SOLAR } from 'pages/dashboard/dashboard.utils';
import { StyledSVG } from './personal-energy-production.style';

interface Props {
  billable?: boolean;
  children?: React.ReactNode;
  copy: {
    body?: string;
    cumulativeSavingsLabel?: string;
    emptyPlaceholder?: string;
    kwhLabel?: string;
    liveBody?: string;
    solarCreditsLabel?: string;
    title?: string;
    allocation?: {
      size: string;
      label: string;
      tooltip: { copy: string; label: string };
    };
  };
  cumulativeVnmSavings?: number | null;
  kwh?: number;
  calculatedAllocationKw?: number | null;
  projectIsLive?: boolean;
  solarCreditAmount?: string | null;
  margin?: Margin;
  originationSourceType?: string;
}

export const PersonalEnergyProduction = ({
  billable,
  children,
  copy,
  cumulativeVnmSavings,
  kwh,
  projectIsLive,
  solarCreditAmount,
  margin,
  calculatedAllocationKw,
  originationSourceType,
}: Props) => {
  const showKwh = kwh !== undefined && kwh > 0;
  const solarCreditIcon =
    solarCreditAmount !== undefined ? DollarSign : DollarSignEmpty;
  const totalSavings = formatAsMoney(cumulativeVnmSavings || 0, {
    conversion: 'fromCents',
  });
  const allocationIcon =
    calculatedAllocationKw === undefined || children ? BlocksEmpty : Blocks;

  const showCumulativeSavings =
    totalSavings && solarCreditAmount && totalSavings > solarCreditAmount;
  const showAllocation = copy.allocation && calculatedAllocationKw;
  const getCopy = copyFor('solar.personalEnergyProduction');
  const sponsoredSolar = originationSourceType === SPONSORED_SOLAR;
  const liveBodyCopy = sponsoredSolar
    ? getCopy('waitlist.sponsoredSolarLiveBody')
    : copy.liveBody;
  const bodyCopy = sponsoredSolar
    ? getCopy('pending.sponsoredSolarBody')
    : copy.body;

  return (
    <Card margin={margin || { bottom: '24px' }} mobileVariant="flat">
      <Card.Header>
        <Text textStyle="heading700" tag="h2">
          {copy.title}
        </Text>
      </Card.Header>
      <Card.Content>
        <Text margin={{ top: '8px' }}>
          {projectIsLive ? liveBodyCopy : bodyCopy}
        </Text>
        {showKwh && (
          <Container flex alignItems="center" margin={{ top: '24px' }}>
            <StyledSVG src={Bolt} />
            <Text
              textStyle="heading700"
              margin={{ left: '16px', right: '8px' }}
            >
              {formatWithCommas(kwh, 0)}
            </Text>
            <Text textStyle="paragraph400">{copy.kwhLabel}</Text>
          </Container>
        )}
        {billable && (
          <Container flex alignItems="center" margin={{ top: '24px' }}>
            <StyledSVG src={solarCreditIcon} />
            <Text
              textStyle="heading700"
              margin={{ left: '16px', right: '8px' }}
            >
              {solarCreditAmount || copy.emptyPlaceholder}
            </Text>
            <Text textStyle="paragraph400">{copy.solarCreditsLabel}</Text>
          </Container>
        )}
        {(showCumulativeSavings || showAllocation) && (
          <Container margin={{ top: '32px' }}>
            <Hr />
            {showCumulativeSavings && (
              <Container flex alignItems="center" margin={{ top: '32px' }}>
                <StyledSVG src={Home} />
                <Text
                  textStyle="heading700"
                  margin={{ left: '16px', right: '8px' }}
                >
                  {totalSavings || copy.emptyPlaceholder}
                </Text>
                <Text textStyle="paragraph400">
                  {copy.cumulativeSavingsLabel}
                </Text>
              </Container>
            )}
            {showAllocation && (
              <Container flex alignItems="center" margin={{ top: '24px' }}>
                <StyledSVG src={allocationIcon} />
                <Text
                  textStyle="heading700"
                  margin={{ left: '16px', right: '8px' }}
                >
                  {copy.allocation!.size}
                </Text>
                <LabelAndTooltip>
                  {copy.allocation!.label}
                  <Tooltip
                    content={copy.allocation!.tooltip.copy}
                    tooltipId="allocationSize"
                    label={copy.allocation!.tooltip.label}
                  >
                    <Icon icon="InfoCircleInverted" aria-hidden={true} />
                  </Tooltip>
                </LabelAndTooltip>
              </Container>
            )}
          </Container>
        )}
        {children && <Container margin={{ top: '24px' }}>{children}</Container>}
      </Card.Content>
    </Card>
  );
};
